/**
 * Firebase Auth emails that may see the full itinerary on /khushi.
 * Add her login email here once you create her Firebase email/password user.
 */
import { doc, getDoc, getFirestore, setDoc, updateDoc } from "firebase/firestore";
import type { KhushiItineraryItem } from "./khushiData";

export const KHUSHI_REVEAL_EMAILS: readonly string[] = [
  "ayush@acchaudhary.com",
];

export function canRevealKhushiItinerary(
  email: string | null | undefined,
): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return KHUSHI_REVEAL_EMAILS.some(
    (e) => e.trim().toLowerCase() === normalized,
  );
}

export type RevealConfig = Record<string, string>;

const LS_KEY = "khushi_reveal_config";
/** Adjust this to change how long reveal config is cached in localStorage. */
export const REVEAL_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;

type CachedRevealConfig = {
  config: RevealConfig;
  expiresAt: number;
};

export type ItineraryConfigDoc = {
  order: string[];
  itemsById: Record<string, KhushiItineraryItem>;
};

const ITINERARY_LS_KEY = "khushi_itinerary_config_v1";
/** Adjust this to change how long itinerary config is cached in localStorage. */
export const ITINERARY_CACHE_TTL_MS = 5 * 60 * 1000;

type CachedItineraryConfig = {
  doc: ItineraryConfigDoc;
  expiresAt: number;
};

function readCachedRevealConfig(): RevealConfig | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedRevealConfig>;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.expiresAt !== "number" ||
      Date.now() >= parsed.expiresAt ||
      !parsed.config ||
      typeof parsed.config !== "object"
    ) {
      return null;
    }
    return parsed.config as RevealConfig;
  } catch {
    return null;
  }
}

function writeCachedRevealConfig(config: RevealConfig) {
  const payload: CachedRevealConfig = {
    config,
    expiresAt: Date.now() + REVEAL_CONFIG_CACHE_TTL_MS,
  };
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch {
    // ignore (private mode / quota / disabled storage)
  }
}

function readCachedItineraryConfig(): ItineraryConfigDoc | null {
  try {
    const raw = localStorage.getItem(ITINERARY_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedItineraryConfig>;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.expiresAt !== "number" ||
      Date.now() >= parsed.expiresAt ||
      !parsed.doc ||
      typeof parsed.doc !== "object" ||
      !Array.isArray((parsed.doc as any).order) ||
      !(parsed.doc as any).itemsById ||
      typeof (parsed.doc as any).itemsById !== "object"
    ) {
      return null;
    }
    return parsed.doc as ItineraryConfigDoc;
  } catch {
    return null;
  }
}

function writeCachedItineraryConfig(docValue: ItineraryConfigDoc) {
  const payload: CachedItineraryConfig = {
    doc: docValue,
    expiresAt: Date.now() + ITINERARY_CACHE_TTL_MS,
  };
  try {
    localStorage.setItem(ITINERARY_LS_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function buildItineraryConfigDoc(
  items: KhushiItineraryItem[],
): ItineraryConfigDoc {
  const order: string[] = [];
  const itemsById: Record<string, KhushiItineraryItem> = {};
  for (const item of items) {
    if (!item?.id) continue;
    if (!itemsById[item.id]) order.push(item.id);
    itemsById[item.id] = item;
  }
  return { order, itemsById };
}

export function itineraryItemsFromDoc(docValue: ItineraryConfigDoc): KhushiItineraryItem[] {
  const itemsById = docValue?.itemsById ?? {};
  const order = Array.isArray(docValue?.order) ? docValue.order : [];
  const out: KhushiItineraryItem[] = [];
  const seen = new Set<string>();
  for (const id of order) {
    const item = itemsById[id];
    if (item && !seen.has(id)) {
      out.push(item);
      seen.add(id);
    }
  }
  for (const [id, item] of Object.entries(itemsById)) {
    if (!seen.has(id) && item) out.push(item);
  }
  return out;
}

export function isItemRevealedFromItem(item: KhushiItineraryItem): boolean {
  const t = item?.revealAt;
  if (!t) return false;
  const parsed = new Date(t).getTime();
  if (Number.isNaN(parsed)) return false;
  return Date.now() >= parsed;
}

export async function fetchItineraryConfig(): Promise<ItineraryConfigDoc | null> {
  const cached = readCachedItineraryConfig();
  if (cached) return cached;
  const snap = await getDoc(doc(getFirestore(), "khushi_config", "itinerary"));
  if (!snap.exists()) return null;
  const docValue = snap.data() as ItineraryConfigDoc;
  if (
    !docValue ||
    !Array.isArray(docValue.order) ||
    !docValue.itemsById ||
    typeof docValue.itemsById !== "object"
  ) {
    return null;
  }
  writeCachedItineraryConfig(docValue);
  return docValue;
}

export async function fetchItineraryConfigFresh(): Promise<ItineraryConfigDoc | null> {
  const snap = await getDoc(doc(getFirestore(), "khushi_config", "itinerary"));
  if (!snap.exists()) return null;
  const docValue = snap.data() as ItineraryConfigDoc;
  if (
    !docValue ||
    !Array.isArray(docValue.order) ||
    !docValue.itemsById ||
    typeof docValue.itemsById !== "object"
  ) {
    return null;
  }
  writeCachedItineraryConfig(docValue);
  return docValue;
}

export async function saveItineraryConfig(docValue: ItineraryConfigDoc): Promise<void> {
  await setDoc(doc(getFirestore(), "khushi_config", "itinerary"), docValue);
  writeCachedItineraryConfig(docValue);
}

export async function replaceItineraryItem(item: KhushiItineraryItem): Promise<void> {
  const itemId = item?.id;
  if (!itemId) throw new Error("item.id is required");
  const ref = doc(getFirestore(), "khushi_config", "itinerary");
  await updateDoc(ref, {
    [`itemsById.${itemId}`]: item,
  });
  try {
    localStorage.removeItem(ITINERARY_LS_KEY);
  } catch {
    // ignore
  }
}

export async function fetchRevealConfig(): Promise<RevealConfig> {
  const cached = readCachedRevealConfig();
  if (cached) {
    return cached;
  }

  const snap = await getDoc(doc(getFirestore(), "khushi_config", "reveals"));
  const config = (snap.exists() ? (snap.data() as RevealConfig) : {}) ?? {};
  writeCachedRevealConfig(config);
  return config;
}

export async function fetchRevealConfigFresh(): Promise<RevealConfig> {
  const snap = await getDoc(doc(getFirestore(), "khushi_config", "reveals"));
  const config = (snap.exists() ? (snap.data() as RevealConfig) : {}) ?? {};
  writeCachedRevealConfig(config);
  return config;
}

export function isItemRevealed(itemId: string, config: RevealConfig): boolean {
  const t = config[itemId];
  if (!t) return false;
  const parsed = new Date(t).getTime();
  const revealed = Date.now() >= parsed;
  return revealed;
}

export async function saveRevealConfig(config: RevealConfig): Promise<void> {
  await setDoc(doc(getFirestore(), "khushi_config", "reveals"), config);
  writeCachedRevealConfig(config);
}
